import { Link, useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type LoginFormProps = {
  className?: string
  emailHint?: string
  paths: {
    forgotPassword: string
    signIn: string
    signUp: string
  }
}

export function LoginForm({
  className,
  emailHint,
  paths,
}: LoginFormProps) {
  const form = useForm({
    email: emailHint ?? "",
    password: "",
  })

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.post(paths.signIn, {
      onFinish: () => form.reset("password"),
    })
  }

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <Card className="rounded-3xl border-border/80 shadow-xl shadow-black/5">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Welcome back</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit}>
            <FieldGroup>
              <Field data-invalid={!!form.errors.email}>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  aria-invalid={!!form.errors.email}
                  autoComplete="email"
                  autoFocus={!emailHint}
                  id="email"
                  name="email"
                  onChange={(event) => form.setData("email", event.target.value)}
                  placeholder="name@example.com"
                  required
                  type="email"
                  value={form.data.email}
                />
                <FieldError>{form.errors.email}</FieldError>
              </Field>
              <Field data-invalid={!!form.errors.password}>
                <div className="flex items-center">
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <Link
                    className="ml-auto text-sm underline-offset-4 hover:underline"
                    href={paths.forgotPassword}
                  >
                    Forgot your password?
                  </Link>
                </div>
                <Input
                  aria-invalid={!!form.errors.password}
                  autoComplete="current-password"
                  autoFocus={!!emailHint}
                  id="password"
                  name="password"
                  onChange={(event) => form.setData("password", event.target.value)}
                  required
                  type="password"
                  value={form.data.password}
                />
                <FieldError>{form.errors.password}</FieldError>
              </Field>
              <Field>
                <Button className="w-full" disabled={form.processing} type="submit">
                  {form.processing ? "Signing in..." : "Sign in"}
                </Button>
                <FieldDescription className="text-center">
                  Don&apos;t have an account?{" "}
                  <Link href={paths.signUp}>Sign up</Link>
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
